import { useEffect, useState } from 'react';

interface TenantValidationState {
  isValid: boolean | null;
  isLoading: boolean;
}

/**
 * Validates a tenant slug through the existing, slug-scoped public bootstrap
 * endpoint. The backend deliberately collapses an unknown slug and a slug
 * owned by another product into the same 404 response.
 */
export function useTenantValidation(tenantSlug: string | undefined): TenantValidationState {
  const [state, setState] = useState<TenantValidationState>({
    isValid: null,
    isLoading: true,
  });

  useEffect(() => {
    if (!tenantSlug) {
      setState({ isValid: false, isLoading: false });
      return;
    }

    const slug = tenantSlug;
    const abortController = new AbortController();

    async function validateTenant(): Promise<void> {
      try {
        const response = await fetch(`/api/${encodeURIComponent(slug)}/validate`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) return;
        setState({ isValid: response.ok, isLoading: false });
      } catch {
        if (abortController.signal.aborted) return;
        setState({ isValid: false, isLoading: false });
      }
    }

    setState({ isValid: null, isLoading: true });
    void validateTenant();

    return () => abortController.abort();
  }, [tenantSlug]);

  return state;
}